<template>
  <b-row class="justify-content-center">
    <b-col md="8">
      <div class="form-card">
        <!-- Section Header -->
        <h2 class="form-title">New Bulk Delete</h2>
        <p class="form-subtitle">Delete all content from a Facebook page</p>

        <b-form @submit.prevent="submitBulkDelete">
          <!-- Access Token Input -->
          <b-form-group
            label="User Access Token"
            label-for="accessToken"
            class="form-group"
          >
            <b-form-input
              id="accessToken"
              v-model="form.accessToken"
              required
              placeholder="Enter your user access token"
              class="form-input"
            ></b-form-input>
          </b-form-group>

          <b-row v-if="user.allowCustomProxy">
            <b-col md="3">
              <span>Use custom proxy?</span>
            </b-col>
            <b-col>
              <b-form-checkbox v-model="form.useCustomProxy"></b-form-checkbox>
            </b-col>
          </b-row>

          <!-- Proxy Details Section -->
          <template v-if="form.useCustomProxy">
            <div class="proxy-section">
              <h6 class="section-title">Proxy Details</h6>
              <b-row>
                <b-col md="6">
                  <b-form-group label="Username" label-for="proxyUsername">
                    <b-form-input
                      id="proxyUsername"
                      v-model="form.proxyUsername"
                      placeholder="Enter username"
                      class="form-input"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Password" label-for="proxyPassword">
                    <b-form-input
                      id="proxyPassword"
                      v-model="form.proxyPassword"
                      type="password"
                      placeholder="Enter password"
                      class="form-input"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group label="Host" label-for="host">
                    <b-form-input
                      id="host"
                      v-model="form.host"
                      placeholder="Enter host"
                      class="form-input"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Port" label-for="port">
                    <b-form-input
                      id="port"
                      v-model="form.port"
                      placeholder="Enter port"
                      class="form-input"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </template>

          <!-- Get Pages Button -->
          <b-button
            type="button"
            @click="fetchPages"
            variant="primary"
            class="w-100 mt-4 action-btn"
          >
            Get Pages
          </b-button>

          <!-- Loading Spinner -->
          <div v-if="loadingPages" class="text-center my-4">
            <b-spinner style="width: 3rem; height: 3rem"></b-spinner>
          </div>

          <!-- Dynamic Fields -->
          <template v-if="pageOptions.length">
            <!-- Page Selection -->
            <b-form-group
              label="Select Page"
              label-for="selectedPage"
              class="form-group mt-4"
            >
              <b-form-select
                id="selectedPage"
                v-model="form.selectedPage"
                :options="pageOptions"
                class="form-select"
                required
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    -- Select a Page --
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <!-- Content Type Selection -->
            <b-form-group
              label="Content Type"
              label-for="contentType"
              class="form-group"
            >
              <b-form-select
                id="contentType"
                v-model="form.contentType"
                :options="contentTypeOptions"
                class="form-select"
                required
              >
              </b-form-select>
            </b-form-group>

            <!-- Delete After Hours -->
            <b-form-group
              label="Delete After (hours)"
              label-for="deleteAfter"
              class="form-group"
            >
              <b-form-input
                id="deleteAfter"
                type="number"
                v-model="form.deleteAfter"
                min="1"
                required
                placeholder="e.g., 24"
                class="form-input"
              ></b-form-input>
              <small class="form-text text-muted">
                Only content older than this will be deleted
              </small>
            </b-form-group>

            <!-- Deletion Interval Configuration -->
            <b-row>
              <b-col md="6">
                <b-form-group
                  label="Interval Min (minutes)"
                  label-for="intervalMin"
                  class="form-group"
                >
                  <b-form-input
                    id="intervalMin"
                    type="number"
                    v-model="form.intervalMin"
                    min="1"
                    required
                    placeholder="e.g., 2"
                    class="form-input"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group
                  label="Interval Max (minutes)"
                  label-for="intervalMax"
                  class="form-group"
                >
                  <b-form-input
                    id="intervalMax"
                    type="number"
                    v-model="form.intervalMax"
                    :min="form.intervalMin"
                    required
                    placeholder="e.g., 10"
                    class="form-input"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
            <small class="form-text text-muted mb-3">
              Time to wait between each deletion to avoid rate limits
            </small>

            <!-- Proxy Toggle -->
            <b-form-group class="form-group">
              <b-form-checkbox v-model="form.useProxy" class="proxy-toggle">
                <strong>Use Proxy for Deletions</strong>
                <br />
                <small class="text-muted">
                  Enable to route deletion requests through proxy (recommended)
                </small>
              </b-form-checkbox>
            </b-form-group>

            <!-- Submit Button -->
            <b-button
              type="submit"
              variant="danger"
              class="w-100 mt-4 submit-btn"
              :disabled="submitting"
            >
              <b-spinner v-if="submitting" small></b-spinner>
              {{ submitting ? "Starting..." : "Start Bulk Delete" }}
            </b-button>
          </template>
        </b-form>
      </div>
    </b-col>
  </b-row>
</template>

<style scoped>
/* Container */
.justify-content-center {
  padding: 30px;
  min-height: 100vh;
  background-color: #f5f7fa;
  border-radius: 12px;
}

.form-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* Typography */
.form-title {
  font-size: 22px;
  font-weight: 600;
  color: #111827;
}

.form-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 20px;
}

/* Input Fields */
.form-group {
  margin-bottom: 20px;
}

.form-input,
.form-select {
  width: 100%;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  padding: 10px;
  font-size: 14px;
  background-color: white;
  transition: border 0.2s ease-in-out;
}

.form-input:focus,
.form-select:focus {
  border-color: #0077ff;
  box-shadow: 0 0 4px rgba(99, 102, 241, 0.2);
}

/* Proxy Section */
.proxy-section {
  background: #f3f4f6;
  padding: 20px;
  border-radius: 8px;
  margin-top: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
}

/* Buttons */
.action-btn {
  background-color: #0077ff;
  color: white;
  font-size: 16px;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
}

.action-btn:hover {
  background-color: #4f46e5;
}

.submit-btn {
  background-color: #dc3545;
  color: white;
  font-size: 16px;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
}

.submit-btn:hover {
  background-color: #c82333;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Proxy Toggle */
.proxy-toggle {
  padding: 15px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

@media (max-width: 768px) {
  .justify-content-center {
    padding: 20px;
  }
}
</style>

<script>
import axios from "axios";

export default {
  data() {
    return {
      form: {
        accessToken: "",
        useCustomProxy: false,
        proxyUsername: "",
        proxyPassword: "",
        host: "",
        port: "",
        selectedPage: null,
        contentType: "both",
        deleteAfter: 24,
        intervalMin: 2,
        intervalMax: 10,
        useProxy: true,
        isIPV4: false,
      },
      pageOptions: [],
      loadingPages: false,
      submitting: false,
      contentTypeOptions: [
        { value: "videos", text: "Videos/Reels Only" },
        { value: "posts", text: "Posts Only" },
        { value: "both", text: "Both Videos and Posts" },
      ],
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },
  },

  methods: {
    async fetchPages() {
      if (!this.form.accessToken) {
        this.$bvToast.toast("Please enter an access token", {
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.loadingPages = true;
      try {
        const response = await axios.post("/api/get-pages", {
          accessToken: this.form.accessToken,
          useCustomProxy: this.form.useCustomProxy,
          proxyUsername: this.form.proxyUsername,
          proxyPassword: this.form.proxyPassword,
          host: this.form.host,
          port: this.form.port,
          isIPV4: this.form.isIPV4,
        });

        if (response.data.success) {
          this.pageOptions = response.data.pages.map((page) => ({
            value: page,
            text: page.name,
          }));
          console.log("Pages fetched:", response.data.pages);
          console.log("Page options:", this.pageOptions);
          this.$bvToast.toast("Pages fetched successfully!", {
            variant: "success",
            solid: true,
          });
        } else {
          this.$bvToast.toast(
            response.data.message || "Failed to fetch pages",
            {
              variant: "danger",
              solid: true,
            },
          );
        }
      } catch (error) {
        this.$bvToast.toast(
          "Error fetching pages: " +
            (error.response?.data?.error || error.message),
          {
            variant: "danger",
            solid: true,
          },
        );
      } finally {
        this.loadingPages = false;
      }
    },

    async submitBulkDelete() {
      if (!this.form.selectedPage) {
        this.$bvToast.toast("Please select a page", {
          variant: "warning",
          solid: true,
        });
        return;
      }

      // Validate intervals
      if (
        parseInt(this.form.intervalMin) < 1 ||
        parseInt(this.form.intervalMax) < 1
      ) {
        this.$bvToast.toast("Intervals must be at least 1 minute", {
          variant: "warning",
          solid: true,
        });
        return;
      }

      if (parseInt(this.form.intervalMin) > parseInt(this.form.intervalMax)) {
        this.$bvToast.toast(
          "Interval Min must be less than or equal to Interval Max",
          {
            variant: "warning",
            solid: true,
          },
        );
        return;
      }

      // Double confirmation
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `This will delete all ${this.form.contentType} from "${this.form.selectedPage.name}" older than ${this.form.deleteAfter} hours. This action cannot be undone. Are you sure?`,
        {
          title: "Confirm Bulk Delete",
          size: "md",
          okVariant: "danger",
          okTitle: "Yes, Delete",
          cancelTitle: "Cancel",
          centered: true,
        },
      );

      if (!confirmed) return;

      this.submitting = true;
      try {
        console.log("Selected page:", this.form.selectedPage);
        console.log(
          "Page has access_token?",
          !!this.form.selectedPage.access_token,
        );

        const payload = {
          userAccessToken: this.form.accessToken,
          pageId: this.form.selectedPage.id,
          pageName: this.form.selectedPage.name,
          pageAccessToken: this.form.selectedPage.access_token,
          contentType: this.form.contentType,
          deleteAfter: parseInt(this.form.deleteAfter),
          intervalMin: parseInt(this.form.intervalMin),
          intervalMax: parseInt(this.form.intervalMax),
          useProxy: this.form.useProxy,
        };

        console.log("Payload being sent:", {
          ...payload,
          pageAccessToken: payload.pageAccessToken
            ? `${payload.pageAccessToken.substring(0, 20)}...`
            : "MISSING",
        });

        // Add custom proxy if enabled
        if (this.form.useCustomProxy) {
          payload.customProxy = {
            host: this.form.host,
            port: parseInt(this.form.port),
            username: this.form.proxyUsername,
            password: this.form.proxyPassword,
            isIPV4: this.form.isIPV4,
          };
        }

        const response = await axios.post("/api/bulk-delete/start", payload);

        this.$bvToast.toast("Bulk delete job started successfully!", {
          variant: "success",
          solid: true,
        });

        // Redirect to job detail
        this.$router.push(`/bulk-delete/${response.data.jobId}`);
      } catch (error) {
        this.$bvToast.toast(
          "Error: " + (error.response?.data?.error || error.message),
          {
            variant: "danger",
            solid: true,
          },
        );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
